import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ContactService, ContactFormData, ContactResponse } from '../../services/contact.service';
import { environment } from '../../../environments/environment';

// Declare grecaptcha for TypeScript
declare var grecaptcha: any;

@Component({
  selector: 'app-contact-us',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './contact-us.html',
  styleUrl: './contact-us.scss'
})
export class ContactUs implements OnInit {
  contactForm: FormGroup;
  isLoading = false;
  showSuccess = false;
  showError = false;
  errorMessage = '';
  captchaCompleted = false;
  captchaError = false;
  captchaToken = '';

  constructor(
    private fb: FormBuilder, 
    private contactService: ContactService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.contactForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern(/^[a-zA-Z ]*$/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      email: ['', [Validators.required, Validators.email]],
      message: ['', [Validators.required]],
      company: ['', [Validators.required]]
    });

    // Populate company from environment config and prevent users from changing it in the UI
    if (environment && environment.companyName) {
      this.contactForm.patchValue({ company: environment.companyName });
    }
    // Make callback functions globally available for reCAPTCHA (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      (window as any).onCaptchaSuccess = (token: string) => this.onCaptchaSuccess(token);
      (window as any).onCaptchaExpired = () => this.onCaptchaExpired();
    }
  }

  ngOnInit() {
    // Ensure reCAPTCHA is loaded (only in browser)
    if (isPlatformBrowser(this.platformId) && typeof grecaptcha !== 'undefined') {
      grecaptcha.ready(() => {
        console.log('reCAPTCHA is ready');
      });
    }
  }

  get f() {
    return this.contactForm.controls;
  }

  onlyNumbers(event: any): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }

  onlyLetters(event: any): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122) || charCode === 32;
  }

  onCaptchaSuccess(token: string) {
    this.captchaCompleted = true;
    this.captchaError = false;
    this.captchaToken = token;
    console.log('reCAPTCHA completed successfully');
  }

  onCaptchaExpired() {
    this.captchaCompleted = false;
    this.captchaError = true;
    this.captchaToken = '';
    console.log('reCAPTCHA expired');
  }

  onSubmit() {
    if (this.contactForm.valid) {
      // Check if reCAPTCHA is completed
      if (!this.captchaCompleted) {
        this.captchaError = true;
        return;
      }

      this.showError = false;
      this.captchaError = false;
      this.isLoading = true;

      const formData: ContactFormData = {
        fullname: this.contactForm.value.firstName,
        email: this.contactForm.value.email,
        mobile: this.contactForm.value.phoneNumber,
        message: this.contactForm.value.message,
        company: this.contactForm.value.company,
        recaptchaToken: this.captchaToken
      };

  this.contactService.submitContactForm(formData).subscribe({
        next: (response: ContactResponse) => {
          this.isLoading = false;
            if (response.status) {
            this.showSuccess = true;
            this.contactForm.reset();
            // Reapply configured company after reset so hidden field remains populated
            if (environment && environment.companyName) {
              this.contactForm.patchValue({ company: environment.companyName });
            }
            this.captchaCompleted = false;
            this.captchaError = false;
            this.captchaToken = '';
            
            // Reset reCAPTCHA
            if (isPlatformBrowser(this.platformId) && typeof grecaptcha !== 'undefined') {
              grecaptcha.reset();
            }
            
            // Hide success message after 3 seconds
            setTimeout(() => {
              this.showSuccess = false;
            }, 3000);
          } else {
            this.showError = true;
            this.errorMessage = response.message || 'Failed to send message. Please try again.';
            
            // Reset reCAPTCHA on error
            if (isPlatformBrowser(this.platformId) && typeof grecaptcha !== 'undefined') {
              grecaptcha.reset();
            }
            this.captchaCompleted = false;
            this.captchaToken = '';
            
            // Hide error message after 5 seconds
            setTimeout(() => {
              this.showError = false;
            }, 5000);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.showError = true;
          this.errorMessage = 'Failed to send message. Please try again.';
          console.error('Error submitting form:', error);
          
          // Reset reCAPTCHA on error
          if (isPlatformBrowser(this.platformId) && typeof grecaptcha !== 'undefined') {
            grecaptcha.reset();
          }
          this.captchaCompleted = false;
          this.captchaToken = '';
          
          // Hide error message after 5 seconds
          setTimeout(() => {
            this.showError = false;
          }, 5000);
        }
      });

      /*
      // For local testing only:
      setTimeout(() => {
        this.isLoading = false;
        this.showSuccess = true;
        this.contactForm.reset();
        if (environment && environment.companyName) {
          this.contactForm.patchValue({ company: environment.companyName });
        }
        setTimeout(() => {
          this.showSuccess = false;
        }, 3000);
      }, 2000);
      */
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.contactForm.controls).forEach(key => {
        this.contactForm.get(key)?.markAsTouched();
      });
    }
  }

  getFieldError(fieldName: string): string {
    const field = this.contactForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return this.getRequiredMessage(fieldName);
      }
      if (field.errors['email']) {
        return 'Enter Correct Email Id';
      }
      if (field.errors['pattern']) {
        return this.getPatternMessage(fieldName);
      }
    }
    return '';
  }

  private getRequiredMessage(fieldName: string): string {
    switch (fieldName) {
      case 'firstName': return 'Enter Name';
      case 'phoneNumber': return 'Enter Phone Number';
      case 'email': return 'Enter Email Address';
      case 'message': return 'Enter Message';
      case 'company': return 'Enter Company Name';
      default: return 'This field is required';
    }
  }

  private getPatternMessage(fieldName: string): string {
    switch (fieldName) {
      case 'firstName': return 'Name should contain only letters';
      case 'phoneNumber': return 'Enter Correct Phone Number (10 digits)';
      default: return 'Invalid format';
    }
  }
}
